import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { API_CONFIG, getHeaders, refreshAccessToken } from "../../config/api";
import { useLoader } from "../../context/loadercontext";
import { RiDeleteBin5Line } from "react-icons/ri";
import { MdEditSquare } from "react-icons/md";
import toast from "react-hot-toast";
import { ChevronDown, ChevronUp } from "lucide-react";
import { DeletePreviewModal, FinalDeleteConfirmModal } from "../common/ui/deletemodal";
import ActionDropdown from "../common/ui/actiondropdown";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import NoFound from '../common/ui/NoFound';
import WarningModal from "../common/ui/warningmodel";

export default function Inbondviewtable({ searchTerm = "", inbondstartDate = "", inbondendDate = "", bestartDate = "", beendDate = "" }) {
  const [openRowIndex, setOpenRowIndex] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [showDeletePreview, setShowDeletePreview] = useState(false);
  const [showFinalDeleteConfirm, setShowFinalDeleteConfirm] = useState(false);
  const [deleteMode, setDeleteMode] = useState(null);
  const [pendingDeleteData, setPendingDeleteData] = useState(null);
  const { setLoading } = useLoader();
  const enableToggle = true;
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [warningVisible, setWarningVisible] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");


  // Move colorPairs outside or use useMemo to prevent recreating on every render
  const colorPairs = useMemo(() => [
    { bg: "#edeff1", text: "#1c2a3d" },
    { bg: "#dbf6e5", text: "#25915a" },
    { bg: "#fff1d6", text: "#ce880d" },
    { bg: "#ffe4de", text: "#b71d18" },
    { bg: "#d6f4f9", text: "#1a7da7" },
  ], []);

  const handleToggle = (idx) => setOpenRowIndex(openRowIndex === idx ? null : idx);

  //Edit Handlers
  const handleEdit = (parentId, childId) => {
    navigate("/in-bond/edit-in-bond", {
      state: { parentId, childId }
    });
  };

  // DELETE HANDLERS
  // Parent delete
  const handleDeleteParent = (id) => {
    const parent = tableData.find((row) => row.id === id);
    if (!parent) return;

    setPendingDeleteData({ parent });
    setDeleteMode("parent");
    setShowDeletePreview(true);
    setShowFinalDeleteConfirm(false);
  };

  // Child delete
  const handleDeleteChild = (parentId, childId) => {
    const parent = tableData.find((row) => row.id === parentId);
    if (!parent) return;

    const child = parent.history.find(
      (c) =>
        c.inbond_child_id === childId ||
        c.inbond_child_master_id === childId ||
        c.id === childId ||
        c.material_master_id === childId
    );
    if (!child) return;

    setPendingDeleteData({ parent, child });
    setDeleteMode("child");
    setShowDeletePreview(true);
    setShowFinalDeleteConfirm(false);
  };

  const handleCancelPreview = () => {
    setShowDeletePreview(false);
    setPendingDeleteData(null);
    setDeleteMode(null);
  };

  const handleProceedToFinalConfirm = () => {
    setShowDeletePreview(false);
    setShowFinalDeleteConfirm(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const fetchController = useRef(null);


  const fetchInbondData = useCallback(async () => {
    setLoading(true);

    if (fetchController.current) {
      fetchController.current.abort();
    }
    fetchController.current = new AbortController();
    const signal = fetchController.current.signal;

    try {
      const url = new URL(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INBOND.GET_ALL}`);

      // Search by BI/BE
      if (searchTerm.trim() !== "") {
        url.searchParams.append("number", searchTerm);
      }
      // Search by In-bond start & end date
      if (inbondstartDate) url.searchParams.append("inbond_start_date", inbondstartDate);
      if (inbondendDate) url.searchParams.append("inbond_end_date", inbondendDate);
      if (bestartDate) url.searchParams.append("be_start_date", bestartDate);
      if (beendDate) url.searchParams.append("be_end_date", beendDate);

      // Helper function for API call
      const callApi = async () =>
        await fetch(url.toString(), {
          method: "GET",
          headers: getHeaders(true),
          signal,
        });

      // Initial API call
      let response = await callApi();

      // -------------------------
      // 🔥 Handle 401 token expired
      // -------------------------
      if (response.status === 401) {
        console.warn("Access token expired. Attempting refresh...");
        const newToken = await refreshAccessToken();

        if (newToken) {
          // Retry API call with refreshed token
          response = await callApi();
        } else {
          console.error("Refresh token failed. Logging out...");
          logout();
          return;
        }
      }

      const result = await response.json();

      if (result.status === 200 && Array.isArray(result.data)) {
        const mappedData = result.data.map((item) => ({
          id: item.id,
          biNumber: item.bi_number,
          beNumber: item.be_number,
          beDate: formatDate(item.be_date),
          inbondDate: formatDate(item.inbond_date),
          history: Array.isArray(item.child)
            ? item.child.map((c) => ({
              inbond_child_id: c.inbond_child_id,
              inbond_child_master_id: c.inbond_child_master_id,
              id: c.id,
              material_master_id: c.material_master_id,
              material_name: c.material_name,
              material_short_code: c.material_short_code,
              weight: `${c.weight.toFixed(2)}`,
              invoiceValue: `$${c.invoice_amount_usd.toFixed(2)}`,
              dollar: `₹ ${c.dollar_inr.toFixed(2)}`,
              price: c.price || "",
              dutyInBond: `₹ ${c.duty_inbond_amount_inr.toFixed(2)}`,
              assessmentValue: `₹ ${c.assessment_amount_inr.toFixed(2)}`,
              totalAmount: `$${c.material_amount_usd.toFixed(2)}`,
              colorIndex: Math.floor(Math.random() * colorPairs.length),
            }))
            : [],
          total_weight: item.total_weight ? Number(item.total_weight).toFixed(2) : "0",
          total_duty_inbond_amount_inr: item.total_duty_inbond_amount_inr
            ? Number(item.total_duty_inbond_amount_inr).toFixed(2)
            : "0",
          total_assessment_amount_inr: item.total_assessment_amount_inr
            ? Number(item.total_assessment_amount_inr).toFixed(2)
            : "0",
          total_material_amount_usd: item.total_material_amount_usd
            ? Number(item.total_material_amount_usd).toFixed(2)
            : "0",
        }));

        setTableData(mappedData);
      } else {
        setTableData([]);
      }
    } catch (error) {
      console.error("Error fetching inbond data:", error);
      setTableData([]);
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  }, [
    setLoading,
    searchTerm,
    inbondstartDate,
    inbondendDate,
    bestartDate,
    beendDate,
    colorPairs.length,
    logout,
  ]);

  const formatIndianNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return "0";
    return Number(num).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };


  const handleConfirmDelete = async () => {
    try {
      setLoading(true);

      if (deleteMode === "parent" && pendingDeleteData?.parent) {
        const inbondId = pendingDeleteData.parent.id;

        let response = await fetch(
          `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INBOND.SOFT_DELETE_COMPLETE(inbondId)}`,
          { method: "PUT", headers: getHeaders(true) }
        );

        // -------------------------
        // 🔥 If ACCESS TOKEN expired
        // -------------------------
        if (response.status === 401) {
          console.warn("Access token expired. Attempting refresh...");

          const newToken = await refreshAccessToken();

          if (newToken) {
            // Retry API call with new token
            response = await fetch(
              `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INBOND.SOFT_DELETE_COMPLETE(inbondId)}`,
              { method: "PUT", headers: getHeaders(true) }
            );
          } else {
            console.error("Refresh token failed. Logging out...");
            logout(); // redirect user
            return;
          }
        }
        const result = await response.json().catch(() => ({}));

        if (response.ok && result.status === 200) {
          toast.success(result.message || "Inbond deleted successfully");
          await fetchInbondData();
        } else {
          setWarningMessage(result.message || "Failed to delete Inbond entry");
          setWarningVisible(true);
        }
      }

      if (deleteMode === "child" && pendingDeleteData?.child) {
        const child = pendingDeleteData.child;
        const childId = child.inbond_child_id || child.inbond_child_master_id || child.id || child.material_master_id;
        let response = await fetch(
          `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INBOND.SOFT_DELETE_PARTIAL(childId)}`,
          { method: "PUT", headers: getHeaders(true) }
        );

        // -------------------------
        // 🔥 If ACCESS TOKEN expired
        // -------------------------
        if (response.status === 401) {
          console.warn("Access token expired. Attempting refresh...");

          const newToken = await refreshAccessToken();

          if (newToken) {
            // Retry API call with new token
            response = await fetch(
              `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INBOND.SOFT_DELETE_PARTIAL(childId)}`,
              { method: "PUT", headers: getHeaders(true) }
            );
          } else {
            console.error("Refresh token failed. Logging out...");
            logout(); // redirect user
            return;
          }
        }
        const result = await response.json().catch(() => ({}));

        if (response.ok && result.status === 200) {
          toast.success(result.message || "Child entry deleted successfully");
          await fetchInbondData();
        } else {
          setWarningMessage(result.message || "Failed to delete child entry");
          setWarningVisible(true);
        }
      }
    } catch (error) {
      console.error("Error deleting Inbond:", error);
      setWarningMessage("Unexpected error while deleting Inbond");
      setWarningVisible(true);
    } finally {
      setLoading(false);
      setShowFinalDeleteConfirm(false);
      setShowDeletePreview(false);
      setPendingDeleteData(null);
      setDeleteMode(null);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchInbondData();
    }, 300); // Debounce API calls by 300ms

    return () => clearTimeout(timer);
  }, [searchTerm, inbondstartDate, inbondendDate, bestartDate, beendDate, fetchInbondData]);

  return (
    <>
      <div className="mt-6 overflow-x-auto">
        <table className="w-full text-[13px] text-center">
          <thead className="border-b border-gray-200 text-gray-600 font-medium">
            <tr>
              {enableToggle && <th className="w-12"></th>}
              <th className="px-4 py-3">BI No.</th>
              <th className="px-4 py-3">BE No.</th>
              <th className="px-4 py-3">BE Date</th>
              <th className="px-4 py-3">In-bond Date</th>
              <th className="px-4 py-3">Total Weight (kg)</th>
              <th className="px-4 py-3">Total Duty In-bond (₹)</th>
              <th className="px-4 py-3">Total Assessment Value (₹)</th>
              <th className="px-4 py-3">Total Amount ($)</th>
              <th className="px-4 py-3">Action</th>
            </tr>
          </thead>

          <tbody>
            {tableData.length === 0 ? (
              <tr>
                <td colSpan={enableToggle ? 10 : 9}>
                  <NoFound />
                </td>
              </tr>
            ) : (
              tableData.map((row, idx) => (
                <React.Fragment key={idx}>
                  <tr
                    className={`cursor-pointer  transition-colors ${openRowIndex === idx ? "bg-gray-50" : ""}`}
                    onClick={() => enableToggle && handleToggle(idx)}
                  >
                    {enableToggle && (
                      <td className="px-3 py-2 text-center">
                        <div className="p-1 rounded hover:bg-gray-200 transition">
                          {openRowIndex === idx ? (
                            <ChevronUp className="w-5 h-5 text-gray-600" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-600" />
                          )}
                        </div>
                      </td>
                    )}
                    <td className="relative px-3 py-2">
                      {/* {enableToggle && openRowIndex === idx && row.history.length > 0 && (
                        <div
                          className="absolute top-full border-l border-gray-500 border-dashed"
                          style={{
                            left: "35%",
                            height: `${row.history.length * 59}px`,
                            transform: "translateX(-50%)",
                          }}
                        >
                          {row.history.map((_, i) => (
                            <div
                              key={i}
                              className="absolute border-t border-gray-500 border-dashed"
                              style={{
                                top: `${(i + 0.5) * 59 + 32}px`,
                                left: "40%",
                                width: "calc(50% + 35px)",
                                transform: "translateX(0)",
                              }}
                            ></div>
                          ))}
                        </div>
                      )} */}
                      {row.biNumber ? row.biNumber.toUpperCase() : "-"}
                    </td>
                    <td className="px-3 py-2">{row.beNumber}</td>
                    <td className="px-3 py-2">{row.beDate}</td>
                    <td className="px-3 py-2">{row.inbondDate}</td>
                    <td className="px-3 py-2">{formatIndianNumber(row.total_weight)}</td>
                    <td className="px-3 py-2">₹ {formatIndianNumber(row.total_duty_inbond_amount_inr)}</td>
                    <td className="px-3 py-2">₹ {formatIndianNumber(row.total_assessment_amount_inr)}</td>
                    <td className="px-3 py-2">$ {formatIndianNumber(row.total_material_amount_usd)}</td>
                    <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                      <ActionDropdown
                        actions={{ edit: true, delete: true }}
                        onEdit={() => handleEdit(row.id, 0)}
                        onDelete={() => handleDeleteParent(row.id)}
                      />
                    </td>
                  </tr>

                  {/* Sub-table */}
                  {enableToggle && openRowIndex === idx && row.history.length > 0 && (
                    <tr>
                      <td></td>
                      <td colSpan={9} className="px-4 py-0">
                        <div className="mt-2 flex justify-center mb-4">
                          <div className="inline-block w-[90%] overflow-hidden">
                            <div className="overflow-x-auto">
                              <table className="w-full text-[13px] text-center">
                                <thead className="border-b border-gray-300 text-gray-500">
                                  <tr>
                                    <th className="px-4 py-2">Material</th>
                                    <th className="px-4 py-2">Weight (kg)</th>
                                    <th className="px-4 py-2">Invoice Value</th>
                                    <th className="px-4 py-2">Dollar</th>
                                    <th className="px-4 py-2">Price</th>
                                    <th className="px-4 py-2">Duty In-bond</th>
                                    <th className="px-4 py-2">Assessment Value</th>
                                    <th className="px-4 py-2">Total Amount</th>
                                    <th className="px-4 py-2">Action</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {row.history.map((h, i) => {
                                    const color = colorPairs[h.colorIndex];
                                    return (
                                      <tr key={i}>
                                        <td className="px-4 py-1">
                                          <div
                                            className="w-14 h-6 flex items-center justify-center font-semibold text-xs text-center rounded uppercase shadow-sm mx-auto"
                                            style={{ backgroundColor: color.bg, color: color.text }}
                                          >
                                            {h.material_short_code}
                                          </div>
                                        </td>
                                        <td className="px-4 py-1">{formatIndianNumber(h.weight)}</td>
                                        <td className="px-4 py-1">{h.invoiceValue.replace(/\d+(\.\d+)?/g, (n) => formatIndianNumber(n))}</td>
                                        <td className="px-4 py-1">{h.dollar.replace(/\d+(\.\d+)?/g, (n) => formatIndianNumber(n))}</td>
                                        <td className="px-4 py-1">{h.price || 0}</td>
                                        <td className="px-4 py-1">{h.dutyInBond.replace(/\d+(\.\d+)?/g, (n) => formatIndianNumber(n))}</td>
                                        <td className="px-4 py-1">{h.assessmentValue.replace(/\d+(\.\d+)?/g, (n) => formatIndianNumber(n))}</td>
                                        <td className="px-4 py-1">{h.totalAmount.replace(/\d+(\.\d+)?/g, (n) => formatIndianNumber(n))}</td>

                                        <td className="px-4 py-1">
                                          <div className="flex items-center justify-center space-x-1">
                                            <button
                                              id={row.history[i]?.id}
                                              onClick={() => handleEdit(row.id, row.history[i]?.id)}
                                              className="text-green-500 hover:text-green-700 p-1" title="Edit Entry">
                                              <MdEditSquare size={18} />
                                            </button>
                                            <button
                                              className="text-red-500 hover:text-red-700 p-1"
                                              title="Delete Entry"
                                              onClick={() =>
                                                handleDeleteChild(row.id, h.inbond_child_id || h.inbond_child_master_id || h.id || h.material_master_id)
                                              }
                                            >
                                              <RiDeleteBin5Line size={18} />
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}

                  <tr>
                    <td colSpan={enableToggle ? 10 : 9} className="border-b border-dashed border-gray-300"></td>
                  </tr>
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modals */}
      {/* Delete preview modal for parent or child */}
      {showDeletePreview && pendingDeleteData && (
        <DeletePreviewModal
          title={deleteMode === "parent" ? "Inbond Details" : "Child Entry Details"}
          data={
            deleteMode === "parent"
              ? {
                "BI No.": pendingDeleteData.parent?.biNumber.toUpperCase(),
                "BE No.": pendingDeleteData.parent?.beNumber,
                "BE Date": pendingDeleteData.parent?.beDate,
                "In-bond Date": pendingDeleteData.parent?.inbondDate,
                "Total Weight (kg)": pendingDeleteData.parent?.total_weight,
                "Total Duty In-bond (₹)": pendingDeleteData.parent?.total_duty_inbond_amount_inr,
                "Total Assessment Value (₹)": pendingDeleteData.parent?.total_assessment_amount_inr,
                "Total Amount ($)": pendingDeleteData.parent?.total_material_amount_usd,
              }
              : {
                "Material": pendingDeleteData.child?.material_short_code.toUpperCase(),
                "Weight": pendingDeleteData.child?.weight,
                "Invoice Value": pendingDeleteData.child?.invoiceValue,
                "Dollar": pendingDeleteData.child?.dollar,
                "Duty In-bond": pendingDeleteData.child?.dutyInBond,
                "Assessment Value": pendingDeleteData.child?.assessmentValue,
                "Total Amount": pendingDeleteData.child?.totalAmount,
              }
          }
          onCancel={handleCancelPreview}
          onProceed={handleProceedToFinalConfirm}
        />
      )}

      {/* Final delete confirm modal */}
      {showFinalDeleteConfirm && pendingDeleteData && (
        <FinalDeleteConfirmModal
          title="Confirm Delete"
          message="Are you sure you want to permanently delete this entry?"
          onCancel={() => {
            setShowFinalDeleteConfirm(false);
            setPendingDeleteData(null);
            setDeleteMode(null);
          }}
          onConfirm={handleConfirmDelete}
        />
      )}
      {warningVisible && (
        <WarningModal
          message={warningMessage}
          onClose={() => setWarningVisible(false)}
        />
      )}

    </>
  );
}